import React from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { canAccessPath } from "./commonFunctions/staffAccess";
import { getHomePath } from "./commonFunctions/homePath";
import { VendorProfileView } from "./FrontEnd/Profile/VendorProfileView";
import VendorProfileEdit from "./FrontEnd/Profile/VendorProfileEdit";
import { VendorProductList } from "./FrontEnd/VendorProduct/VendorProductList";
import VendorProductCreate from "./FrontEnd/VendorProduct/VendorProductCreate";
import { VendorTypeList } from "./FrontEnd/VendorType/VendorTypeList";
import { VendorType } from "./FrontEnd/VendorType/VendorType";
import { DashBoard } from "./FrontEnd/Dashboard/Dashboard";
import FormBuilder from "./test/FormBuilder";
import FormPreview from "./test/FormPreview";
import { ProductView } from "./FrontEnd/VendorProduct/ProductView";
import { LabTestList, LabTestsOnlyList, LabPackagesList } from "./FrontEnd/Lab/LabTestPackage/LabTestList";
import { LabTestPackage } from "./FrontEnd/Lab/LabTestPackage/LabTest";
import { LabOrdersList, LabOrderHistory } from "./FrontEnd/Lab/LabOrders/LabOrdersList";
import { LabOrderDetails } from "./FrontEnd/Lab/LabOrders/LabOrderDetails";
import { RevenuePaymentDetails } from "./FrontEnd/Lab/RevenuePayments/Revenue";
import RevenuePaymentsDashboard from "./FrontEnd/Lab/RevenuePayments/RevenuePayment";
import { PharmacyOrderList, PharmacyOrderHistory } from "./FrontEnd/Pharmacy/PharmacyOrder/PharmacyOrderList";
import GeneralSettings from "./FrontEnd/Settings/GeneralSettings";
import StaffPermissions from "./FrontEnd/Settings/Staff/StaffPermissions";
import { PharmacyOrderDetails } from "./FrontEnd/Pharmacy/PharmacyOrder/PharmacyOrdersDetails";
import { PrescriptionOrderDetails } from "./FrontEnd/Pharmacy/PharmacyOrder/PrescriptionOrderDetails";
import { PaymentList } from "./FrontEnd/Pharmacy/Payment/PaymentList";
import PaymentDetailsCard from "./FrontEnd/Pharmacy/Payment/PaymentDetails";
import { TechnicianManagement } from "./FrontEnd/Pharmacy/TechnicianManagement/TechnicianManagement";
import { AssignOrder } from "./FrontEnd/Pharmacy/TechnicianManagement/AssignOrder";
import { TechnicianDetails } from "./FrontEnd/Pharmacy/TechnicianManagement/TechnicianDetails";
import { LabReport } from "./FrontEnd/Lab/Report/LabReport";
import { LabOverview } from "./FrontEnd/Lab/LabOverview/LabOverview";
import { LabNotifications } from "./FrontEnd/Lab/LabNotifications/LabNotifications";
import { LabAssignOrder } from "./FrontEnd/Lab/LabTechnician/LabAssignOrder";
import { LabTechnicianDetails } from "./FrontEnd/Lab/LabTechnician/LabTechnicianDetails";
import { ScheduleManagement } from "./FrontEnd/Lab/ScheduleManagement/ScheduleManagement";
import LabTechnicianManage from "./FrontEnd/Lab/LabTechnician/LabTechnicianManage";
import LabReportViewer from "./FrontEnd/Lab/RevenuePayments/LabReportView";
import PrescriptionBillCreate from "./FrontEnd/Pharmacy/PharmacyOrder/PrescriptionBill";
import LabScanList from "./FrontEnd/Lab/LabTestPackage/LabScanList";
import LabScanOrderDetails from "./FrontEnd/Lab/LabTestPackage/LabScanOrderDetails";
import LabScanCreate from "./FrontEnd/Lab/LabTestPackage/ScanCategoryCreate";
import ScanTestList from "./FrontEnd/Lab/LabTestPackage/ScanCategoryList";
import ScanSubCategoryList from "./FrontEnd/Lab/LabTestPackage/ScanSubCategoryList";
import ScanSubCategoryCreate from "./FrontEnd/Lab/LabTestPackage/ScanSubCategoryCreate";
import LabCategoryList from "./FrontEnd/Lab/LabCategory/LabCategoryList";
import { ScanCategoryListing } from "./FrontEnd/Lab/LabTestPackage/ScanCategoryListing";
import ScanCategory from "./FrontEnd/Lab/LabTestPackage/ScanCategory";
import ScanCategoryList from "./FrontEnd/Lab/LabTestPackage/ScanCategoryList";

interface PrivateRouteProps {
  token: string | null;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ token }) => {
  const { pathname } = useLocation();
  if (!token) {
    return token ? <Outlet /> : <Navigate to="/login" />;
  }
  // Staff can't open pages of modules they weren't given, even by typing the address
  if (!canAccessPath(pathname)) {
    const home = getHomePath();
    if (!canAccessPath(home)) {
      // Nothing usable is enabled for this account, so there is nowhere to redirect to
      return (
        <div style={{ padding: 32 }}>
          No modules are enabled for your account. Please contact your administrator.{" "}
          <a href="/logout">Log out</a>
        </div>
      );
    }
    return <Navigate to={home} replace />;
  }
  const stt: any = [
    {
      id: "8f425908-9ac4-4113-af56-4107cfd1d7a1",
      title: "Basci Information",
      controls: [
        {
          id: "f8197f8a-864a-4a04-b61d-f5cf9879c774",
          type: "text",
          label: "Text Input",
          name: "text",
          sm: 4,
        },
        {
          id: "62355d63-1513-4a60-895d-379e69c862b5",
          type: "checkbox",
          label: "Checkbox",
          name: "checkbox",
          sm: 4,
        },
      ],
    },
    {
      id: "6002aeca-4a5b-4456-9428-5e3719456dda",
      title: "Details",
      controls: [
        {
          id: "fc3e9f00-0955-4e0b-8aa5-adb63ba6ab1e",
          type: "dropdown",
          label: "Dropdown",
          name: "dropdown",
          sm: 4,
          options: ["Option 1", "Option 2"],
        },
      ],
    },
  ];

  return (
    <Routes>
      {/* <Route path="/" element={<Layout />}/> */}
      <Route path="/dashboard" element={<DashBoard />} />

      <Route path="/profile/view" element={<VendorProfileView />} />
      <Route path="/profile/edit/:userId" element={<VendorProfileEdit />} />

      <Route path="/vendor-product/list" element={<VendorProductList />} />
      <Route path="/vendor-product/add" element={<VendorProductCreate />} />
      <Route
        path="/vendor-product/edit/:id"
        element={<VendorProductCreate />}
      />

      <Route path="/vendor-product-view/:id" element={<ProductView />} />

      <Route path="/vendortype/list" element={<VendorTypeList />} />
      <Route path="/vendortype/add" element={<VendorType />} />
      <Route path="/vendortype/edit/:id" element={<VendorType />} />

      <Route path="/labtestpackage/list" element={<LabTestList />} />
      <Route path="/lab-tests/list" element={<LabTestsOnlyList />} />
      <Route path="/lab-packages/list" element={<LabPackagesList />} />
      <Route path="/lab-test-package/add" element={<LabTestPackage />} />
      <Route path="/lab-test-package/:id" element={<LabTestPackage />} />

      <Route path="/lab-orders/list" element={<LabOrdersList />} />
      <Route path="/lab-orders/history" element={<LabOrderHistory />} />
      {/* <Route path="/lab-order-details" element={<LabOrderDetails />} /> */}
      <Route path="/lab-orders/list/view/:id" element={<LabOrderDetails />} />

      <Route
        path="/revenue-details/:bookingId"
        element={<RevenuePaymentDetails />}
      />
      <Route path="/revenue-payments" element={<RevenuePaymentsDashboard />} />

      <Route path="/pharmacy-orders/list" element={<PharmacyOrderList />} />
      <Route path="/pharmacy-orders/history" element={<PharmacyOrderHistory />} />

      <Route path="/settings/general" element={<GeneralSettings />} />
      <Route path="/settings/staff" element={<StaffPermissions />} />
      {/* Old Roles & Permissions address */}
      <Route path="/settings/roles" element={<Navigate to="/settings/staff" replace />} />
      <Route
        path="/pharmacy-order-details/:id"
        element={<PharmacyOrderDetails />}
      />
      <Route
        path="/prescription-order-details/:id"
        element={<PrescriptionOrderDetails />}
      />
      <Route
        path="/technician-management/list"
        element={<TechnicianManagement />}
      />
      <Route path="/assign-order/order/:id" element={<AssignOrder />} />
      <Route path="/payment/list" element={<PaymentList />} />

      <Route
        path="/prescription/:id/bill-create"
        element={<PrescriptionBillCreate />}
      />

      <Route path="/technician-details/:id" element={<TechnicianDetails />} />

      <Route path="/lab-report/list" element={<LabReport />} />
      {/* <Route path="/Uploads/lab-reports/:id" element={<LabReport />} /> */}
      <Route path="/lab-overview" element={<LabOverview />} />
      <Route path="/lab-notifications" element={<LabNotifications />} />
      <Route path="/pharmacy-notifications" element={<LabNotifications />} />
      <Route path="/labreport/view" element={<LabReportViewer />} />

      <Route
        path="/lab-technician-manage/list"
        element={<LabTechnicianManage />}
      />
      <Route path="/lab-assign-order/:id" element={<LabAssignOrder />} />
      <Route
        path="/lab-technician-details"
        element={<LabTechnicianDetails />}
      />
      <Route
        path="/lab-technician-details/:id"
        element={<LabTechnicianDetails />}
      />

      <Route path="/lab-schedule" element={<ScheduleManagement />} />
      <Route path="/lab-scanning/list" element={<LabScanList />} />
      <Route path="/lab-scanning/view/:id" element={<LabScanOrderDetails />} />
     
      <Route path="/scan-test/:id" element={<LabScanCreate/>}/>
      <Route path="/scan-test/add" element={<LabScanCreate/>}/>
      <Route path="/scan-test/list" element={<ScanTestList/>}/>
      <Route path="/scan-sub-category/list" element={<ScanSubCategoryList/>}/>
      <Route path="/scan-sub-category/add" element={<ScanSubCategoryCreate/>}/>
      <Route path="/scan-sub-category/:id" element={<ScanSubCategoryCreate/>}/>

      <Route path="/scan-category/add" element={<ScanCategory/>}/>
      <Route path="/scan-category/:id" element={<ScanCategory/>}/>
      <Route path="/scan-category/list" element={<ScanCategoryListing/>}/>
      <Route path="/lab-category/list" element={<LabCategoryList />}/>


      <Route path="/test" element={<FormBuilder />} />
      <Route path="/prev" element={<FormPreview structure={stt} />} />
    </Routes>
  );
};

export default PrivateRoute;
